import { cn, Icon, type IconName } from "@nexus/ui";

export interface SettingsRailItem<TId extends string> {
  id: TId;
  icon: IconName;
  label: string;
  /** Draws the unsaved marker: this group holds an edit the footer counts. */
  isChanged: boolean;
}

interface SettingsRailProps<TId extends string> {
  /** Namespaces the tab and panel ids, so two rails can share a page. */
  idPrefix: string;
  label: string;
  /** Read out for the unsaved marker; passed in so the copy stays translated. */
  unsavedLabel: string;
  items: ReadonlyArray<SettingsRailItem<TId>>;
  active: TId;
  onChange: (id: TId) => void;
}

export const settingsRailTabId = (prefix: string, id: string) => `${prefix}-tab-${id}`;
export const settingsRailPanelId = (prefix: string, id: string) => `${prefix}-panel-${id}`;

/**
 * Which group of a settings page is open.
 *
 * A rail rather than another row of pills: the page-level tabs and the settings
 * switcher above it are already two horizontal strips, and a third would read
 * as one long undifferentiated bar. Below `md` it wraps into pills, where
 * vertical space is the scarce thing instead.
 */
export function SettingsRail<TId extends string>({
  idPrefix,
  label,
  unsavedLabel,
  items,
  active,
  onChange,
}: SettingsRailProps<TId>) {
  return (
    <nav
      role="tablist"
      aria-orientation="vertical"
      aria-label={label}
      className="flex shrink-0 flex-row flex-wrap gap-1 md:w-56 md:flex-col"
    >
      {items.map((item) => {
        const isActive = item.id === active;

        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={settingsRailTabId(idPrefix, item.id)}
            aria-selected={isActive}
            aria-controls={settingsRailPanelId(idPrefix, item.id)}
            onClick={() => onChange(item.id)}
            className={cn(
              "flex items-center gap-2 rounded-md px-3 py-2 text-left text-xs transition-colors",
              "focus-visible:outline-2 focus-visible:outline-ring md:w-full",
              // Carried by every item so the active one does not shift the row.
              "border-l-2 border-transparent",
              isActive
                ? "border-l-primary bg-primary/10 font-semibold text-primary dark:bg-primary/20"
                : "font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground",
            )}
          >
            <Icon name={item.icon} size="sm" color={isActive ? "primary" : "muted"} aria-hidden />
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {/* The footer counts changes across every group, so without this
                marker it reports a number with no way to find what it means. */}
            {item.isChanged ? (
              <span
                className="size-1.5 shrink-0 rounded-full bg-primary"
                aria-label={unsavedLabel}
              />
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
